import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useAnalytics } from '@/hooks/analytics';
import { ProductInfo } from '@/hooks/analytics/types';
import { usePaymentFormValues } from '@/hooks/purchase/usePaymentFormValues';
import { initLoggableSubscriptionDetails } from '@/hooks/purchase/usePurchaseSuccess';
import { useBrowserLanguageState, usePricesState, usePurchaseParamsState, useUserState } from '@/hooks/store';
import { isValidPlan } from '@/store/purchaseParams/types';
import { setUser } from '@/store/user/actions';
import { Subscription } from '@/store/user/types';
import {
	createUpsellPurchaseCompletedProps,
	UPSELL_PURCHASE_COMPLETED,
} from '@/utils/promoOffer/createUpsellPurchaseCompletedProps';

import { createSuccessEventProps } from '../PaymentElementsForm/utils';
import { type CheckoutSuccessPayload } from './persistSuccessPayload';

const PROMO_PARAM_KEYS = ['flavor', 'variant', 'pack_class', 'coupon'] as const;

const snapshotPromoParams = (
	searchParams: { get: (name: string) => string | null } | null,
): Record<string, string> =>
	Object.fromEntries(
		PROMO_PARAM_KEYS.flatMap(key => {
			const value = searchParams?.get(key);
			return value ? [[key, value]] : [];
		}),
	);

export type UseCheckoutSessionPurchaseSuccessProps = {
	productInfo?: ProductInfo;
	successArgs?: Record<string, unknown>;
	// Attribution parity with the rest of the CS funnel events: the Success event name uses
	// `analyticsPrefix` (default 'Subscribe : Purchase : Form') and `analyticsEventProps` is merged
	// into its props — matching `${analyticsPrefix} : Success` + `...analyticsEventProps` in legacy.
	analyticsPrefix?: string;
	analyticsEventProps?: Record<string, string>;
};

/**
 * Reuses the control flow's `createSuccessEventProps` machinery so the Checkout
 * Sessions purchase event is field-for-field identical to control (treatment ==
 * control for the A/B comparison). The money/plan fields come from redux
 * (`prices` / `purchaseParams`), never from a backend amount.
 *
 * - `buildPayload` snapshots those redux-derived props once (stable
 *   `amp_order_id`) so the same payload drives both the on-page success event
 *   and the persisted redirect-fallback replay.
 * - `fireSuccess` updates user state (which routes SimplifiedSignup to Handoff)
 *   and logs the purchase + Success event, adding the subscription-derived
 *   details from the freshly-polled active subscription.
 */
export default function useCheckoutSessionPurchaseSuccess({
	productInfo: productInfoOverride,
	successArgs,
	analyticsPrefix = 'Subscribe : Purchase : Form',
	analyticsEventProps,
}: UseCheckoutSessionPurchaseSuccessProps = {}) {
	const dispatch = useDispatch();
	const user = useUserState();
	const prices = usePricesState();
	const purchaseParams = usePurchaseParamsState();
	const browserLanguage = useBrowserLanguageState();
	const { paymentFormCurrency } = usePaymentFormValues();
	const { logEvent, logEventAsync, logPurchase } = useAnalytics();
	const searchParams = useSearchParams();

	const buildPayload = useCallback(
		(selectedPaymentType: string): CheckoutSuccessPayload => {
			const productInfo: ProductInfo = productInfoOverride ?? {
				name: 'web_subscription',
				sku: purchaseParams.plan,
				unit_price: purchaseParams?.purchaseType?.price ? purchaseParams.purchaseType.price / 100.0 : 0,
				quantity: 1,
			};

			const moneyEventProps = createSuccessEventProps({
				productInfo,
				prices,
				purchaseParams,
				selectedPaymentType,
				paymentFormCurrency,
				browserLanguage,
				userEmail: user?.email,
				successArgs: {
					is_stripe_next: true,
					source: searchParams?.get('source') ?? '',
					...successArgs,
				},
			});

			return {
				// `${analyticsPrefix} : Success` (default 'Subscribe : Purchase : Form'). Baked into
				// the payload here so the redirect-fallback replay (/checkout/return) fires the same
				// name even though it reconstructs from the persisted payload.
				eventName: `${analyticsPrefix} : Success`,
				// `${analyticsPrefix} : Pending` baked in for the same reason — the `/checkout/return`
				// hook instance is constructed with no args, so its closure prefix defaults; passing
				// this through `firePending`'s `eventNameOverride` keeps the return-path Pending event
				// under the caller's prefix, symmetric with the replayed Success event.
				pendingEventName: `${analyticsPrefix} : Pending`,
				productInfo,
				transactionId: user?.id ?? null,
				moneyEventProps,
				// Baked in too so the 3DS-return replay keeps the attribution props — the
				// `/checkout/return` hook instance is constructed with no args, so its closure
				// `analyticsEventProps` is undefined; `fireSuccess` reads the payload's copy first.
				analyticsEventProps,
				// The offer link's params are gone after the 3DS redirect, so the upsell
				// conversion event replayed on `/checkout/return` reads this copy.
				promoParams: snapshotPromoParams(searchParams),
				...(isValidPlan(purchaseParams.plan) && prices.current?.[purchaseParams.plan]
					? { shownPrice: { amount: prices.current[purchaseParams.plan], currency: paymentFormCurrency } }
					: {}),
			};
		},
		[
			productInfoOverride,
			prices,
			purchaseParams,
			paymentFormCurrency,
			browserLanguage,
			user?.email,
			user?.id,
			searchParams,
			successArgs,
			analyticsPrefix,
			analyticsEventProps,
		],
	);

	// Funnel signal for a paid-but-not-yet-server-confirmed purchase (Stripe "processing":
	// confirm() succeeded but `subscriptions/active` hasn't materialized within the poll
	// window). Deliberately NOT the confirmed event: it does NOT call `logPurchase` (Amplitude
	// Revenue) and uses a DISTINCT event name (so no GTM/Segment conversion rule keyed on the
	// 'Success' event matches) with NO money/value props — so it can't book revenue or fire a
	// value-bearing ad-platform conversion on unconfirmed fulfillment.
	//
	// PROPOSAL: there is no prior pending/processing purchase-event convention in
	// the codebase — this name + prop set should be confirmed with analytics/PM. The
	// non-negotiable property (no confirmed/value event on unconfirmed fulfillment) holds
	// regardless of the final naming.
	const firePending = useCallback(
		async ({
			selectedPaymentType,
			planOverride,
			sourceOverride,
			analyticsEventPropsOverride,
			eventNameOverride,
		}: {
			selectedPaymentType?: string;
			planOverride?: string | null;
			sourceOverride?: string;
			analyticsEventPropsOverride?: Record<string, string>;
			eventNameOverride?: string;
		} = {}): Promise<void> => {
			await logEventAsync({
				// `${analyticsPrefix} : Pending` — KEEP the distinct `: Pending` suffix (NOT `:
				// Success`) so no GTM/Segment conversion rule keyed on the Success event matches on
				// unconfirmed fulfillment. `eventNameOverride` (the persisted `pendingEventName`) lets
				// the `/checkout/return` path keep the caller's prefix even though its hook instance
				// has no args — same reason it passes `analyticsEventPropsOverride`.
				eventName: eventNameOverride ?? `${analyticsPrefix} : Pending`,
				eventProps: {
					// `planOverride`/`sourceOverride` let the redirect-return path (where redux
					// purchaseParams + the live query string are reset post-redirect) pass the plan
					// and source from the persisted payload instead — keeping the pending event's
					// `source` symmetric with the replayed Success event (which keeps it from the
					// persisted `moneyEventProps.source`).
					plan: planOverride ?? purchaseParams.plan ?? null,
					payment_type: selectedPaymentType ?? null,
					is_stripe_next: true,
					source: sourceOverride ?? searchParams?.get('source') ?? '',
					// Attribution parity. On-page this hook closes over `analyticsEventProps`; the
					// `/checkout/return` instance is constructed with no args (closure is undefined),
					// so it passes `analyticsEventPropsOverride` from the persisted payload — same
					// reason `fireSuccess` prefers `payload.analyticsEventProps`.
					...(analyticsEventPropsOverride ?? analyticsEventProps),
				},
			});
		},
		[logEventAsync, purchaseParams.plan, searchParams, analyticsPrefix, analyticsEventProps],
	);

	const fireSuccess = useCallback(
		async (payload: CheckoutSuccessPayload | null, subscription?: Subscription): Promise<void> => {
			// A subscription that is PRESENT but `valid:false` is the stale-sub signal —
			// `subscriptions/active` selects the most-relevant row by `expires`, not validity, so during
			// a resubscribe window it can 200 with a lapsed/canceled sub. On the PayPal path (where the
			// returned sub is the ONLY fulfillment signal) that must never drive a confirmed-conversion
			// (Success) or Revenue (`logPurchase`) event, nor enrich one with the stale sub's details.
			// Downgrade to the unconfirmed Pending shape (no `logPurchase`, no money/value props, a
			// distinct event name so no GTM/Segment conversion rule matches). An ABSENT sub (undefined)
			// is unchanged — the poll-lag case where the caller has already server-confirmed fulfillment
			// (card: `requireValid` poll; 3DS: session-status poll) and the sub is enrichment that just
			// hasn't materialized. The 3DS caller passes a stale invalid sub as `undefined` for exactly
			// this reason, so it still fires its confirmed event unenriched — see CheckoutReturnClient.
			if (subscription && !subscription.valid) {
				await firePending({
					selectedPaymentType:
						typeof payload?.moneyEventProps?.payment_type === 'string'
							? payload.moneyEventProps.payment_type
							: undefined,
					planOverride: payload?.productInfo.sku ?? null,
					sourceOverride:
						typeof payload?.moneyEventProps?.source === 'string' ? payload.moneyEventProps.source : undefined,
					analyticsEventPropsOverride: payload?.analyticsEventProps,
					eventNameOverride: payload?.pendingEventName,
				});
				return;
			}

			// Updating the user with a valid subscription is what routes SimplifiedSignup
			// to its next screen (Handoff) on the on-page path. When fulfillment lags past
			// the poll window the subscription isn't available yet — we still fire the
			// analytics event (payment is confirmed), but skip `setUser` so we don't
			// clobber user state with an undefined subscription.
			// Require `valid`: storing a `valid:false` sub (e.g. a stale canceled one
			// returned by `subscriptions/active`, which selects by `expires` not validity) would
			// fail the Handoff gate (`subscription.valid === true`) and loop the flow back to payment.
			if (subscription?.valid) {
				dispatch(setUser({ ...user, subscription }));
			}

			if (!payload) {
				// DEGRADED conversion: the purchase is server-confirmed fulfilled, but the persisted
				// analytics payload (redux-derived money/plan, baked on the payment page) was
				// unavailable on `/checkout/return` — sessionStorage evicted across the off-site
				// redirect, or the buyer returned in a different context. A fulfilled purchase must
				// still fire its conversion event, so we log `${analyticsPrefix} : Success` here too
				// rather than silently dropping it. We deliberately do NOT fabricate money/value
				// props: those come only from the client redux snapshot, never the
				// backend (the session-status and active-subscription responses carry no purchase
				// amount/currency), so they are simply absent — and we skip `logPurchase` (Amplitude
				// Revenue) so no $0/garbage revenue is booked. Conversion rules keyed on the Success
				// event name still fire; `payload_recovered: false` lets analytics measure how often
				// this degraded path runs. The caller (`/checkout/return`) logs a warning too.
				await logEventAsync({
					eventName: `${analyticsPrefix} : Success`,
					eventProps: {
						is_stripe_next: true,
						payload_recovered: false,
						// Enrich only with a VALID sub (belt-and-braces past the stale-sub guard).
						...(subscription?.valid ? initLoggableSubscriptionDetails(subscription) : {}),
						...analyticsEventProps,
					},
				});
				return;
			}

			logPurchase({ productInfo: payload.productInfo, transactionInfo: { id: payload.transactionId } });

			await logEventAsync({
				eventName: payload.eventName,
				eventProps: {
					...payload.moneyEventProps,
					// Enrich only with a VALID sub (belt-and-braces past the stale-sub guard).
					...(subscription?.valid ? initLoggableSubscriptionDetails(subscription) : {}),
					// Prefer the payload's baked-in copy (survives the 3DS redirect) over this hook
					// instance's closure (undefined on `/checkout/return`).
					...(payload.analyticsEventProps ?? analyticsEventProps),
				},
			});

			const promoParams = payload.promoParams ?? {};
			const upsellPurchaseProps =
				payload.productInfo.sku === 'gift_card'
					? null
					: createUpsellPurchaseCompletedProps({
							searchParams: { get: name => promoParams[name] ?? null },
							subscription: subscription?.valid ? subscription : undefined,
							plan: payload.productInfo.sku,
							browserLanguage,
							shownPrice: payload.shownPrice,
						});
			// Not awaited, so a slow analytics call cannot hold up the post-purchase redirect.
			if (upsellPurchaseProps) {
				logEvent({ eventName: UPSELL_PURCHASE_COMPLETED, eventProps: upsellPurchaseProps });
			}
		},
		[
			dispatch,
			user,
			logPurchase,
			logEvent,
			logEventAsync,
			firePending,
			analyticsPrefix,
			analyticsEventProps,
			browserLanguage,
		],
	);

	return { buildPayload, fireSuccess, firePending };
}
